import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { IconButton } from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import * as S from './styles';
import { Button, Checkbox, Option, SingleSelect, Table } from '../../../../../_core/_components';
import { SlipItem } from '../../types';
import api from '../../../../../_core/api';
import { failure, handleErrors } from '../../../../../_core/services/toast';
import { FullScreenLoading } from '../../../../../_core/_components/fullscreen-loading';
import ModalNegociar from './modal-negociar';
import ModalExpectativa from './modal-expectativa';
import { renderSubItems } from './sub-items';
import ModalMultiBoletos from './modal-multi-boletos';

const CURRENT_MONTH = moment().month() + 1;
const CURRENT_YEAR = moment().year();

export function SaldoAberto({
  stateBoletos,
  getBoletos,
}: Readonly<{
  stateBoletos: [SlipItem[], React.Dispatch<React.SetStateAction<SlipItem[]>>];
  getBoletos: () => Promise<void>;
}>): JSX.Element {
  const [boletos, setBoletos] = stateBoletos;
  const [selectedMonth, setSelectedMonth] = useState<Option>();
  const [monthOptions, setMonthOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [negociarOpen, setNegociarOpen] = useState(false);
  const [expectativaOpen, setExpectativaOpen] = useState(false);
  const [expectativaMultiOpen, setExpectativaMultiOpen] = useState(false);

  const boletosSelecionados = boletos.filter(b => b.isSelected);
  const getSelectedBolsCount = boletos.filter(b => b.isSelected).length;

  const currentMonthSlips = boletos?.filter(e => {
    const [year, month] = e.dueDate.split('-');
    return month === CURRENT_MONTH.toString().padStart(2, '0') && year === CURRENT_YEAR.toString();
  });
  const currentMonthSlipsIDs = currentMonthSlips?.map(e => e.slipId);
  const pastSlips = boletos?.filter(e => !currentMonthSlipsIDs?.includes(e.slipId));
  const pastMonthSlipsIDs = pastSlips.map(e => e.slipId);

  const getUniqueDatesByMonth = (data: SlipItem[]) => {
    const uniqueDates = new Set<string>();

    data.forEach(item => {
      const formattedDate = moment(item.dueDate).format('MM/YYYY');
      uniqueDates.add(formattedDate);
    });

    return Array.from(uniqueDates).sort((a, b) => moment(b, 'MM/YYYY').valueOf() - moment(a, 'MM/YYYY').valueOf());
  };

  useEffect(() => {
    if (monthOptions.length === 0 && renderedDataPastMonths) {
      const dates = getUniqueDatesByMonth(renderedDataPastMonths).map(v => ({
        value: v,
        label: v,
      }));
      setMonthOptions(dates);
    }
  }, [boletos]);

  const filterItemsByMonth = (data: SlipItem[], monthYear: string) => {
    return data.filter(item => {
      const itemMonthYear = moment(item.dueDate).format('MM/YYYY');
      return itemMonthYear === monthYear;
    });
  };

  const setTransactions = (bol: SlipItem, isOpen: boolean, transacoes?: any[]) => {
    const transactionsArr = transacoes?.map((e: any) => ({
      ...e.tituloAr,
      valorDescontoMulta: e.valorDescontoMulta,
      valorDescontoJuros: e.valorDescontoJuros,
      valorCorrecaoMonetaria: e.valorCorrecaoMonetaria,
    }));
    const newBoletos = boletos?.map(b => {
      if (b.slipNumber === bol.slipNumber) {
        return {
          ...b,
          isOpen,
          transacoes: transactionsArr || [],
        };
      }
      return b;
    });

    setBoletos(newBoletos);
  };

  const toggleBoleto = (bol: SlipItem) => {
    const newBoletos = boletos?.map(b => {
      if (b.slipId === bol.slipId) {
        return { ...b, isSelected: !b.isSelected };
      }
      return b;
    });

    setBoletos(newBoletos);
  };

  const handleOpenBoleto = async (boleto: SlipItem) => {
    try {
      if (!boleto.isOpen) {
        setLoading(true);
        const { data } = await api.get(`/api/allos/v1/prioritization/detail/slipId/${boleto.slipId}/titles`);
        setTransactions(boleto, true, data);
      } else {
        setTransactions(boleto, false);
        return;
      }
    } catch (e) {
      handleErrors(e);
    }
    setLoading(false);
  };

  const renderedData = useMemo(() => {
    if (currentMonthSlips) {
      return currentMonthSlips.map(e => ({
        ...e,
        arrow: (
          <IconButton aria-label="expand row" size="small" onClick={() => handleOpenBoleto(e)}>
            {e.isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        ),
        checkbox: <Checkbox onClick={() => toggleBoleto(e)} checked={e.isSelected ?? false} />,
        slipValueStr: e.slipValue?.toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        dueDateStr: moment(e.dueDate).format('DD/MM/YYYY'),
        subItems: e.isOpen && e.transacoes ? renderSubItems(e.transacoes) : undefined,
      }));
    }
    return [];
  }, [boletos]);

  const renderedDataPastMonths = useMemo(() => {
    if (pastSlips) {
      let rend: SlipItem[] = pastSlips.map(e => ({
        ...e,
        slipValueStr: e.slipValue?.toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        arrow: (
          <IconButton aria-label="expand row" size="small" onClick={() => handleOpenBoleto(e)}>
            {e.isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        ),
        checkbox: <Checkbox onClick={() => toggleBoleto(e)} checked={e.isSelected || false} />,
        dueDateStr: moment(e.dueDate).format('DD/MM/YYYY'),
        subItems: e.isOpen && e.transacoes ? renderSubItems(e.transacoes) : undefined,
      }));

      // Filtra caso tenha filtro selecionado
      if (selectedMonth) {
        rend = filterItemsByMonth(rend, selectedMonth.label);
      }

      return rend;
    }
    return [];
  }, [boletos, filterItemsByMonth]);

  const handleSelectAllClick = (currentMonth: boolean) => {
    const arrBoletos = [...boletos];

    const isSelectedAll = currentMonth
      ? renderedData.every(e => e.isSelected)
      : renderedDataPastMonths.every(e => e.isSelected);

    const newValue = !isSelectedAll;
    const validIds = currentMonth ? currentMonthSlipsIDs : pastMonthSlipsIDs;

    const updatedBoletos = arrBoletos.map(item => ({
      ...item,
      isSelected: validIds.includes(item.slipId) ? newValue : item.isSelected,
    }));

    setBoletos(updatedBoletos);
  };

  const handleClickExpectativa = () => {
    if (getSelectedBolsCount === 1) {
      return setExpectativaOpen(true);
    }
    return setExpectativaMultiOpen(true);
  };

  const handleNegociar = () => {
    if (boletosSelecionados.length > 0) return setNegociarOpen(true);
    return failure('Selecione ao menos um boleto');
  };

  return (
    <>
      <FullScreenLoading isEnabled={loading} />
      {negociarOpen && (
        <ModalNegociar
          onRequestUpdate={getBoletos}
          boletos={boletosSelecionados}
          onClose={() => setNegociarOpen(false)}
        />
      )}

      {expectativaMultiOpen && (
        <ModalMultiBoletos
          onRequestUpdate={getBoletos}
          boletos={boletosSelecionados}
          onClose={() => setExpectativaMultiOpen(false)}
        />
      )}

      {expectativaOpen && (
        <ModalExpectativa
          boletos={boletosSelecionados}
          onClose={() => setExpectativaOpen(false)}
          onRequestUpdate={getBoletos}
          companyId={boletosSelecionados[0].idEmpresa}
        />
      )}

      <S.MonthIndicationHeading>
        <h3>Mês vigente</h3>
      </S.MonthIndicationHeading>
      {renderedData.length > 0 && (
        <Table
          columns={[
            { label: '', key: 'arrow' },
            {
              label: (
                <Checkbox
                  type="secondary"
                  checked={renderedData.every(e => e.isSelected)}
                  onClick={() => handleSelectAllClick(true)}
                />
              ),
              key: 'checkbox',
            },
            { label: 'Shopping', key: 'nomeShop' },
            { label: 'Boleto', key: 'slipNumber' },
            { label: 'LUC', key: 'luc' },
            { label: 'Marca', key: 'brand' },
            { label: 'Valor', key: 'slipValueStr' },
            { label: 'Vencimento', key: 'dueDateStr' },
            { label: 'Cartas/Email', key: 'statusCartaEmail' },
            { label: 'Serasa', key: 'statusSerasa' },
            { label: 'Ajuizamento', key: 'statusAjuizamento' },
            { label: 'Status Negociação', key: 'typeNegotiation' },
            { label: 'Status Expectativa', key: 'statusExpectativa' },
          ]}
          data={renderedData}
        />
      )}
      <S.MonthIndicationHeading>
        <h3>Meses anteriores</h3>
        <SingleSelect
          isClearable
          hideLabel
          placeholder="Selecione um mês"
          options={monthOptions}
          state={[selectedMonth, setSelectedMonth]}
        />
      </S.MonthIndicationHeading>

      {renderedDataPastMonths.length > 0 && (
        <Table
          columns={[
            { label: '', key: 'arrow' },
            {
              label: (
                <Checkbox
                  type="secondary"
                  checked={renderedDataPastMonths.every(e => e.isSelected)}
                  onClick={() => handleSelectAllClick(false)}
                />
              ),
              key: 'checkbox',
            },
            { label: 'Shopping', key: 'nomeShop' },
            { label: 'Boleto', key: 'slipNumber' },
            { label: 'LUC', key: 'luc' },
            { label: 'Marca', key: 'brand' },
            { label: 'Valor', key: 'slipValueStr' },
            { label: 'Vencimento', key: 'dueDateStr' },
            { label: 'Cartas/Email', key: 'statusCartaEmail' },
            { label: 'Serasa', key: 'statusSerasa' },
            { label: 'Ajuizamento', key: 'statusAjuizamento' },
            { label: 'Status Negociação', key: 'typeNegotiation' },
            { label: 'Status Expectativa', key: 'statusExpectativa' },
          ]}
          data={renderedDataPastMonths}
        />
      )}

      <S.Separator />
      <S.ActionsWrapper>
        <S.Actions>
          <S.ActionLabel>Ações</S.ActionLabel>
          <S.ActionRelatorios>
            <S.ActionButtons>
              <Button
                disabled={getSelectedBolsCount === 0}
                onClick={handleClickExpectativa}
                text="Expectativa de Recebimento"
              />
              <Button disabled={boletosSelecionados.length === 0} onClick={handleNegociar} text="Negociar" />
            </S.ActionButtons>
          </S.ActionRelatorios>
        </S.Actions>
      </S.ActionsWrapper>
    </>
  );
}
